import { useRef } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Kbd } from '@/components/ui/kbd';
import { useBusqueda } from '@/hooks/use-busqueda';
import { useScopedHotkey } from '@/hooks/use-scoped-hotkey';
import { cn } from '@/lib/utils';

/**
 * EL campo de búsqueda de la aplicación. Uno solo, y lo usan todas las listas y
 * todos los tableros.
 *
 * El comportamiento —esperar a que alguien deje de teclear, no preguntar por
 * una letra sola, no repetir una pregunta ya contestada, limpiar al tiro— vive
 * entero en `useBusqueda`; acá está lo que se ve y lo que se oye. La división
 * importa: `FilterBar` dibuja este campo adentro de su barra, pero el tablero
 * de Empresas y la mesa de ayuda lo ponen sueltos, y si el comportamiento
 * estuviera en la barra cada tablero tendría que volver a escribirlo. Fue
 * exactamente lo que pasó: había cuatro campos de búsqueda distintos.
 *
 * LO QUE ESTE CAMPO NO HACE es decidir si hubo resultados. Eso lo contesta el
 * servidor y lo dice la pantalla; acá abajo sólo aparece el aviso de que
 * todavía no se preguntó nada, porque ése sí es un estado DEL CAMPO.
 *
 * Tenía además una `nota` para que la pantalla escribiera ahí su propia línea.
 * La usaba una sola —el contador del tablero de Empresas— y el dueño la sacó,
 * así que se fue con ella: un hueco de texto libre debajo del buscador de toda
 * la aplicación, sin nadie que lo llene, es una invitación a que cada pantalla
 * vuelva a inventar su propia línea. Contactos y Leads ponen la suya como hijo
 * de `FilterBar`, que es otro lugar y otra cosa.
 */
export interface BuscadorProps {
  /** El término que el servidor está contestando. Normalmente `list.params.q`. */
  valor: string;
  onCambiar: (termino: string) => void;
  placeholder?: string;
  /**
   * Cómo se llama este campo para un lector de pantalla.
   *
   * Aparte del `placeholder` porque son dos cosas distintas: el `placeholder`
   * dice qué escribir («Buscar por nombre o dominio…») y la etiqueta dice qué
   * se busca («Buscar empresas»). Sin esto, dos campos en la misma pantalla se
   * anuncian igual.
   */
  etiqueta?: string;
  /** Hay una consulta en vuelo. Cambia la lupa por el indicador de espera. */
  buscando?: boolean;
  /** Milisegundos desde la última tecla. `0` manda en cada tecla. */
  retardo?: number;
  /** Mínimo de caracteres. `1` lo apaga para una lista que aguanta una letra. */
  minimo?: number;
  /** El atajo `/`, acotado a este campo. Apagado donde ya haya otro. */
  atajo?: boolean;
  className?: string;
}

export function Buscador({
  valor,
  onCambiar,
  placeholder = 'Buscar…',
  etiqueta,
  buscando = false,
  retardo,
  minimo,
  atajo = true,
  className,
}: BuscadorProps) {
  // Al campo se llega por su envoltorio: `Input` es un componente de atributos
  // en el contrato de `components/ui` y no reenvía `ref`.
  const caja = useRef<HTMLDivElement>(null);
  const enfocar = () => caja.current?.querySelector('input')?.focus();

  const busqueda = useBusqueda({
    valor,
    onCambiar,
    ...(retardo === undefined ? {} : { retardo }),
    ...(minimo === undefined ? {} : { minimo }),
  });

  // Acotado y no global: una pantalla de lista-con-ficha, o una lista adentro
  // de un panel, monta DOS buscadores, y un `/` suelto le daría el foco al que
  // se montó último en vez de al que la persona está mirando.
  useScopedHotkey('/', enfocar, { containerRef: caja, enabled: atajo });

  // El indicador de espera reemplaza a la lupa en vez de sumarse: son el mismo
  // dato —qué está haciendo el campo— y dos íconos a la vez es ruido.
  const Icono = buscando ? Loader2 : Search;

  const aviso = busqueda.insuficiente
    ? `Escribí al menos ${busqueda.minimo} caracteres para buscar.`
    : undefined;

  return (
    // EL MÍNIMO VA ACÁ AFUERA, no en la caja del campo.
    //
    // Estaba adentro —`min-w-48` en la caja, `min-w-0` en el envoltorio—, y ésa
    // es la receta del desborde: el envoltorio aceptaba encogerse hasta cero,
    // así que el flex de la barra le daba menos de 192px y el campo se salía
    // por encima del control de al lado en vez de empujarlo. En la lista de
    // Empresas a 1440px el buscador quedaba montado sobre «Industria».
    //
    // Con el mínimo afuera, el flex lo respeta: la barra parte el renglón antes
    // de encoger el campo más allá de lo que se puede leer. No cambia el ancho
    // donde hay espacio, que es la única diferencia visible en las demás
    // pantallas. Y sigue siendo el valor POR OMISIÓN: `cn` resuelve el
    // conflicto a favor del `className` de quien llama, así que la bandeja de
    // Correo (`min-w-0`) y la mesa de ayuda (`min-w-52`) mandan igual que antes.
    <div className={cn('flex min-w-48 flex-col gap-1', className)}>
      <div ref={caja} className="relative flex-1">
        <Icono
          className={cn(
            'pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground',
            buscando && 'animate-spin',
          )}
          aria-hidden
        />
        <Input
          type="search"
          value={busqueda.texto}
          onChange={(evento) => busqueda.escribir(evento.target.value)}
          onKeyDown={(evento) => {
            // `Escape` limpia, y sólo si hay algo que limpiar: en un campo
            // vacío el `Escape` es de quien esté más afuera —un panel, una
            // ficha— y quedárselo dejaría un diálogo que no se cierra.
            if (evento.key === 'Escape' && busqueda.texto) {
              evento.stopPropagation();
              busqueda.limpiar();
            }
          }}
          placeholder={placeholder}
          aria-label={etiqueta ?? placeholder}
          // WebKit dibuja su propio ✕ adentro de `type="search"`, justo al lado
          // del nuestro: dos botones de limpiar pegados, uno de ellos sin
          // estilo. `type="search"` igual vale la pena por la semántica y por
          // el teclado del teléfono, así que lo que se va es el glifo nativo.
          className="pl-8 pr-9 shadow-inner-sm [&::-webkit-search-cancel-button]:appearance-none"
        />
        {busqueda.texto ? (
          <button
            type="button"
            aria-label="Limpiar búsqueda"
            onClick={() => {
              busqueda.limpiar();
              // El foco se queda acá: quien limpia casi siempre va a escribir
              // otra cosa, y mandarlo al principio del documento lo obliga a
              // volver con el mouse.
              enfocar();
            }}
            className="absolute right-2 top-1/2 grid size-4 -translate-y-1/2 place-items-center rounded-sm text-muted-foreground transition-colors duration-150 ease-out hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        ) : (
          atajo && (
            <Kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">/</Kbd>
          )
        )}
      </div>

      {/* Se anuncia solo: aparece después de que la persona dejó de escribir,
          cuando ya no está mirando el campo. */}
      {aviso && (
        <p role="status" aria-live="polite" className="text-xs text-muted-foreground">
          {aviso}
        </p>
      )}
    </div>
  );
}
